/**
 * voice-v8 — Werkzeugkarten: Web: Suche, Tiefenrecherche, Amazon, Link-Vorschau, Gegenprobe.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { ART_KLASSE, baueKarte, fehlerBlock, kartenTitel, kuerzeListe, liste, listenEintrag, weitereHinweis } from "../karte.js";
import type { ToolCardMessage } from "../webrtc.js";
import { kuerzeFuerAnzeige } from "../../../shared/html-text";
import { menschlicherFehler } from "../fehler-worte.js";
import { hostVon, ohneEigeneKarte, proxiedImg, toolLabel, trimText, unwrapResult } from "./hilfe.js";

interface SearchResult {
  query?: string;
  results?: Array<{ title: string; url: string; description: string }>;
}

// 2026-07-05 (Martin): Recherche-Karte fuer deep_research (Perplexity) —
// die belegte Antwort erzaehlt Vespera im Text; hier die anklickbaren Quellen.
export function buildDeepResearchCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const q = typeof r.query === "string" ? r.query : "";
  const el = baueKarte("deep-research");
  el.appendChild(kartenTitel(q ? `Recherche \u00b7 ${q}` : "Recherche"));
  if (r.ok === false) {
    el.appendChild(
      fehlerBlock(
        typeof r.error === "string" ? r.error : "Recherche fehlgeschlagen",
      ),
    );
    return el;
  }
  const sources = Array.isArray(r.sources)
    ? (r.sources as Array<{ url?: string; title?: string }>)
    : [];
  if (sources.length > 0) {
    const lbl = document.createElement("div");
    lbl.className = "tool-dr-label";
    lbl.textContent = `Quellen \u00b7 ${sources.length}`;
    el.appendChild(lbl);
    const list = document.createElement("ol");
    list.className = "tool-dr-sources";
    for (const s of sources.slice(0, 8)) {
      if (!s.url) continue;
      let host = s.url;
      try {
        host = new URL(s.url).hostname.replace(/^www\./, "");
      } catch {
        /* keine gueltige URL */
      }
      const li = document.createElement("li");
      const a2 = document.createElement("a");
      a2.href = s.url;
      a2.target = "_blank";
      a2.rel = "noopener";
      a2.className = "tool-dr-source";
      a2.textContent = s.title ? s.title : host;
      li.appendChild(a2);
      if (s.title) {
        const dom = document.createElement("span");
        dom.className = "tool-dr-domain";
        dom.textContent = host;
        li.appendChild(dom);
      }
      list.appendChild(li);
    }
    el.appendChild(list);
  } else {
    const none = document.createElement("div");
    none.className = "tool-browser-detail";
    none.textContent = "Recherche mit Quellen (Perplexity).";
    el.appendChild(none);
  }
  return el;
}

/*
 * 2026-08-24 (Martin, nach einer Arztsuche mit vier Suchlaeufen):
 *
 *   "Außerdem interessiert mich die ganze Suchanfrage nicht. Ich möchte nur das
 *   Suchergebnis haben, so wie es bei ChatGPT ist und bei anderen schlanken
 *   Systemen. Was brauche ich da? Tausende Suchanfragen haben."
 *
 *   Jede Suche warf sechs Treffer mit Titel und Beschreibung in den Verlauf.
 *   Vier Suchlaeufe hintereinander — vierundzwanzig Eintraege, durch die er sich
 *   scrollen musste, um die eine Antwort zu finden. Die Antwort selbst war zwei
 *   Zeilen lang.
 *
 * WARUM NICHT GANZ WEG
 *
 *   Weil man nachsehen koennen muss, WORAUF eine Antwort sich stuetzt. Genau in
 *   dieser Arztsuche war das entscheidend: Das Modell hatte "Arzt" als
 *   "Zahnarzt" gelesen und in Deutschland gesucht — sichtbar nur an den
 *   Treffern. Sie zu loeschen hiesse, solche Fehler unsichtbar zu machen.
 *
 *   Deshalb zusammengeklappt statt entfernt: eine Zeile, ein Tipp zum
 *   Aufklappen. <details> macht das ohne eine Zeile JavaScript.
 */
export function buildSearchCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as SearchResult;
  const el = baueKarte("search");
  const treffer = (r.results ?? []).slice(0, 6);

  const box = document.createElement("details");
  box.className = "tool-search-details";
  const kopf = document.createElement("summary");
  kopf.className = "tool-search-summary";
  const anzahl = treffer.length;
  const rest =
    (r.query ? ` · ${r.query}` : "") +
    (anzahl ? `  ·  ${anzahl} ${anzahl === 1 ? "Quelle" : "Quellen"}` : "");
  /*
   * 23.09.2026 (#2418): „Web-Suche · “ ist ein eigenes Stück (ART_KLASSE) —
   * in der Faltung „Websuche · 5-mal“ nennt das der Kopf, dort schweigt es.
   * Es trägt den ersten Trenner mit, damit danach keine Zeile mit „·“
   * beginnt. Ein Umschlag hält die Zeile EIN Stück im flex-Kopf wie bisher.
   */
  const trenner = /^\s*·\s*/.exec(rest)?.[0] ?? "";
  const zeile = document.createElement("span");
  const art = document.createElement("span");
  art.className = ART_KLASSE;
  art.textContent = `Web-Suche${trenner}`;
  zeile.append(art, rest.slice(trenner.length));
  kopf.appendChild(zeile);
  box.appendChild(kopf);
  el.appendChild(box);

  const list = document.createElement("ul");
  list.className = "tool-search-results";
  for (const hit of treffer) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = hit.url;
    a.target = "_blank";
    a.rel = "noopener";
    a.className = "tool-search-title";
    a.textContent = hit.title || hit.url;
    const desc = document.createElement("div");
    desc.className = "tool-search-snippet";
    desc.textContent = hit.description ?? "";
    li.appendChild(a);
    li.appendChild(desc);
    list.appendChild(li);
  }
  box.appendChild(list);
  return el;
}

interface AmazonProductShape {
  title?: string;
  url?: string;
  asin?: string;
}
interface AmazonSearchShape {
  query?: string;
  products?: AmazonProductShape[];
  search_url?: string;
}

/**
 * Pre.541: Amazon-Treffer als Karte mit ECHTEN, klickbaren Produkt-Links
 * (direkt aus dem amazon_search-Result). So muss der Bot die URL nicht im
 * Text reproduzieren — das verhindert halluzinierte/kaputte /dp/-Links.
 */
export function buildAmazonSearchCard(result: unknown): HTMLElement {
  const el = baueKarte("amazon");
  const r = unwrapResult(result) as AmazonSearchShape;
  el.appendChild(kartenTitel("Amazon-Treffer"));

  const products = Array.isArray(r.products) ? r.products : [];
  if (products.length === 0) {
    const empty = document.createElement("div");
    empty.className = "amazon-empty";
    empty.textContent = "Kein direktes Produkt gefunden.";
    el.appendChild(empty);
  }

  const list = document.createElement("div");
  list.className = "amazon-list";
  for (const p of products.slice(0, 6)) {
    if (!p.url) continue;
    const row = document.createElement("a");
    row.className = "amazon-item";
    row.href = p.url;
    row.target = "_blank";
    row.rel = "noopener";
    const t = document.createElement("span");
    t.className = "amazon-item-title";
    t.textContent = p.title || p.url;
    row.appendChild(t);
    const open = document.createElement("span");
    open.className = "amazon-item-open";
    open.textContent = "Öffnen";
    row.appendChild(open);
    list.appendChild(row);
  }
  el.appendChild(list);

  if (r.search_url) {
    const all = document.createElement("a");
    all.className = "amazon-search-all";
    all.href = r.search_url;
    all.target = "_blank";
    all.rel = "noopener";
    all.textContent = "Alle Treffer auf Amazon";
    el.appendChild(all);
  }
  return el;
}

/**
 * Freien Text in eine Karte setzen — als Liste, wenn er eine ist.
 *
 * Pre.797: Aufzaehlungstext („N gefunden:\n• A — B — C") gehoert als echte
 * Liste gerendert, nicht als 400-Zeichen-Textwurst.
 *
 * ⚠️ 10.09.2026 (Ticket 2205) — herausgeloest aus {@link buildGenericCard}.
 *    Die Gegenprobe-Karte braucht genau dasselbe, und eine zweite Kopie
 *    waere die naechste Stelle, die auseinanderlaeuft. Martin: *„Cards
 *    grundsaetzlich sollten alle einheitlich sein."*
 */
function zeigeText(el: HTMLElement, text: string, maxBlock = 400): void {
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const bullets = lines.filter((l) => /^[•\-–]\s+/.test(l));
  if (bullets.length >= 2) {
    const intro = lines.find((l) => !/^[•\-–]\s+/.test(l));
    if (intro) {
      const head = document.createElement("div");
      head.className = "tool-browser-detail";
      head.textContent = trimText(intro, 160);
      el.appendChild(head);
    }
    const ul = liste(el);
    const gekuerzt = kuerzeListe(bullets, 8);
    for (const b of gekuerzt.sichtbar) {
      ul.appendChild(listenEintrag(trimText(b.replace(/^[•\-–]\s+/, ""), 160)));
    }
    weitereHinweis(el, gekuerzt.rest);
    return;
  }
  const body = document.createElement("div");
  body.className = "tool-browser-detail";
  body.textContent = trimText(text, maxBlock);
  el.appendChild(body);
}

/**
 * Die Gegenprobe — was jemand gegen die These vorbringt. (10.09.2026)
 *
 * Martin: *„Die Karte zeigt als Beschriftung ‚Neubehauptung'. Das ist kein
 * verstaendlicher Titel."* — Sie hatte gar keine eigene Karte und fiel in
 * die allgemeine Schluessel-Wert-Liste. Dort stand das INTERNE Feld
 * `neueBehauptung` als Beschriftung; es sagt Vespera nur, ob eine zweite
 * Runde noch etwas braechte, und geht Martin nichts an.
 *
 * ⚠️ Keine eigenen Stile: dieselbe `baueKarte()`/`kartenTitel()`/`zeigeText()`-Kette
 *    wie ueberall. Eine neue CSS-Regel neben die alte zu setzen ist das
 *    Fehlermuster vom 27.07., und Martin hat ausdruecklich Einheitlichkeit
 *    verlangt.
 */
export function buildGegenprobeCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("gegenprobe");
  el.appendChild(kartenTitel("Gegenprobe"));
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    el.appendChild(
      fehlerBlock(
        menschlicherFehler(
          r,
          "Die Gegenprobe kam gerade nicht zustande.",
        ),
      ),
    );
    return el;
  }
  const these = typeof r.these === "string" ? r.these.trim() : "";
  if (these) {
    const zeile = document.createElement("div");
    zeile.className = "tool-browser-detail";
    zeile.textContent = `Geprüft: ${trimText(these, 160)}`;
    el.appendChild(zeile);
  }
  const gegenrede = typeof r.gegenrede === "string" ? r.gegenrede.trim() : "";
  // 15.10.2026: 900 statt 1.200 — mit der These bleibt die Karte unter der Karten-Größe (1.200 sichtbar).
  if (gegenrede) zeigeText(el, gegenrede, 900);
  else el.appendChild(fehlerBlock("Keine Gegenrede erhalten."));
  return el;
}

/**
 * Eine gelesene Webseite als LINK-VORSCHAU. (18.09.2026)
 *
 * Martin, mit einem Foto der alten Karte: *„und auch das hier! da sollte das
 * Bild von der Seite sein, daneben der Text! Weisst du was ich meine?!"*
 *
 * Die alte Karte war ein Datenblatt: `url`, `titel`, `text`, `zeichen 20451`,
 * `gekuerzt false` — fuenf Zeilen, von denen drei Innenleben waren. Das hier
 * ist die Form, die jeder aus jedem Messenger kennt: Bild links, Titel und
 * Anriss rechts, die Herkunft klein darunter.
 *
 * ⚠️ Das Bild laeuft ueber /api/img und NIE direkt. Die Adresse kommt aus
 *    einer fremden Seite; der Proxy loest den Namen auf und sperrt interne
 *    Ziele (server/image-proxy.ts, gehaertet seit 15.07.2026).
 */
export function buildLinkVorschau(msg: ToolCardMessage): HTMLElement {
  const r = unwrapResult(msg.result);
  if (r.ok === false || (typeof r.error === "string" && r.error)) {
    return ohneEigeneKarte(msg);
  }
  const url = typeof r.url === "string" ? r.url : "";
  const titel = typeof r.titel === "string" ? r.titel.trim() : "";
  const text = typeof r.text === "string" ? r.text.trim() : "";
  const bild = typeof r.bild === "string" ? r.bild : "";
  if (!url && !titel && !text) return ohneEigeneKarte(msg);

  const el = baueKarte("link-vorschau");
  el.appendChild(kartenTitel(toolLabel(msg.tool)));

  const a = document.createElement("a");
  a.className = "link-vorschau";
  a.href = url || "#";
  a.target = "_blank";
  a.rel = "noopener noreferrer";

  if (bild) {
    const box = document.createElement("div");
    box.className = "link-vorschau-bild";
    const im = document.createElement("img");
    im.src = proxiedImg(bild, url);
    im.alt = "";
    im.loading = "lazy";
    // Laedt das Bild nicht, verschwindet der Platz — kein grauer Kasten.
    im.addEventListener("error", () => box.remove());
    box.appendChild(im);
    a.appendChild(box);
  }

  const spalte = document.createElement("div");
  spalte.className = "link-vorschau-text";

  const h = document.createElement("div");
  h.className = "link-vorschau-titel";
  h.textContent = titel || hostVon(url) || "Webseite";
  spalte.appendChild(h);

  if (text) {
    const p = document.createElement("p");
    p.className = "link-vorschau-anriss";
    p.textContent = kuerzeFuerAnzeige(text, 240);
    spalte.appendChild(p);
  }

  const quelle = document.createElement("div");
  quelle.className = "link-vorschau-quelle";
  quelle.textContent = hostVon(url);
  spalte.appendChild(quelle);

  a.appendChild(spalte);
  el.appendChild(a);
  return el;
}
